import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import CheatSheet from '../../components/admin/CheatSheet';
import { Building2, Check, Clock, Loader2, Search, Eye } from 'lucide-react';
import { apiClient } from '../../api/client';

const STATUS_STYLES = {
  pending_approval: { bg: '#FEF3C7', color: '#92400E', label: 'Pending' },
  active: { bg: '#ECFDF5', color: '#059669', label: 'Active' },
  rejected: { bg: '#FEF2F2', color: '#DC2626', label: 'Rejected' },
  expired: { bg: '#F0F0F0', color: '#6B6257', label: 'Expired' },
};

const AdminCorporates = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [approving, setApproving] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const loadCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiClient.request('/api/corporate/admin/all');
      setCompanies(data.companies || []);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { loadCompanies(); }, [loadCompanies]);

  const approveCompany = async (companyId) => {
    setApproving(companyId);
    try {
      await apiClient.request(`/api/corporate/admin/approve/${companyId}`, { method: 'POST' });
      await loadCompanies();
    } catch (e) { alert(e.message); }
    setApproving(null);
  };

  const filtered = companies
    .filter(c => filter === 'all' || c.status === filter)
    .filter(c => !search || c.company_name?.toLowerCase().includes(search.toLowerCase()) || c.admin_email?.toLowerCase().includes(search.toLowerCase()));

  const pending = companies.filter(c => c.status === 'pending_approval').length;
  const active = companies.filter(c => c.status === 'active').length;

  return (
    <AdminLayout pageTitle="Corporates">
      <CheatSheet pageId="corporates" />
      <div data-testid="admin-corporates-page">
        {/* Stats row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl p-5" style={{ border: '1px solid #E0E0E0' }}>
            <p className="text-xs" style={{ color: '#6B6257' }}>Total Companies</p>
            <p className="text-3xl font-bold" style={{ color: '#1A1209' }}>{companies.length}</p>
          </div>
          <div className="bg-white rounded-xl p-5" style={{ border: '1px solid #E0E0E0' }}>
            <div className="flex items-center gap-2">
              <Clock size={14} style={{ color: '#F59E0B' }} />
              <p className="text-xs" style={{ color: '#6B6257' }}>Pending Approval</p>
            </div>
            <p className="text-3xl font-bold" style={{ color: '#F59E0B' }}>{pending}</p>
          </div>
          <div className="bg-white rounded-xl p-5" style={{ border: '1px solid #E0E0E0' }}>
            <div className="flex items-center gap-2">
              <Check size={14} style={{ color: '#2EAA5C' }} />
              <p className="text-xs" style={{ color: '#6B6257' }}>Active</p>
            </div>
            <p className="text-3xl font-bold" style={{ color: '#2EAA5C' }}>{active}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1" style={{ maxWidth: 320 }}>
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#6B6257' }} />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by name or email..." className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }}
              data-testid="corporate-search" />
          </div>
          {['all', 'pending_approval', 'active'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className="px-3 py-2 rounded-lg text-xs font-medium transition-all"
              style={filter === f ? { backgroundColor: '#F5831F', color: 'white' } : { backgroundColor: '#F0F0F0', color: '#666' }}
              data-testid={`filter-${f}`}>
              {f === 'all' ? 'All' : f === 'pending_approval' ? 'Pending' : 'Active'}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-12"><Loader2 size={24} className="animate-spin mx-auto" style={{ color: '#F5831F' }} /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-sm" style={{ color: '#6B6257' }}>No companies found.</div>
        ) : (
          <div className="space-y-3">
            {filtered.map(company => {
              const st = STATUS_STYLES[company.status] || STATUS_STYLES.expired;
              const expanded = expandedId === company.company_id;
              const pilotEnd = company.pilot_end ? new Date(company.pilot_end) : null;
              const daysLeft = pilotEnd ? Math.max(0, Math.ceil((pilotEnd - new Date()) / 86400000)) : null;
              return (
                <div key={company.company_id} className="bg-white rounded-xl overflow-hidden" style={{ border: '1px solid #E0E0E0' }} data-testid={`company-row-${company.company_id}`}>
                  <div className="flex items-center justify-between px-5 py-4">
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#2EAA5C15' }}>
                        <Building2 size={20} style={{ color: '#2EAA5C' }} />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-sm font-bold truncate" style={{ color: '#1A1209' }}>{company.company_name}</h3>
                        <p className="text-xs" style={{ color: '#6B6257' }}>{company.industry} | {company.company_size} employees | {company.admin_email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="px-2.5 py-1 rounded-full text-[10px] font-semibold" style={{ backgroundColor: st.bg, color: st.color }}>{st.label}</span>
                      {daysLeft !== null && company.status === 'active' && (
                        <span className="text-xs" style={{ color: daysLeft <= 14 ? '#DC2626' : '#6B6257' }}>{daysLeft}d left</span>
                      )}
                      {company.status === 'pending_approval' && (
                        <button onClick={() => approveCompany(company.company_id)} disabled={approving === company.company_id}
                          className="px-4 py-2 rounded-lg text-xs font-semibold text-white" style={{ backgroundColor: '#2EAA5C' }}
                          data-testid={`approve-company-${company.company_id}`}>
                          {approving === company.company_id ? <Loader2 size={14} className="animate-spin" /> : 'Approve'}
                        </button>
                      )}
                      <button onClick={() => setExpandedId(expanded ? null : company.company_id)}
                        className="p-2 rounded-lg hover:bg-gray-50" data-testid={`expand-company-${company.company_id}`}>
                        <Eye size={14} style={{ color: '#6B6257' }} />
                      </button>
                    </div>
                  </div>
                  {expanded && (
                    <div className="px-5 pb-4 border-t" style={{ borderColor: '#F0F0F0', backgroundColor: '#FAFAFA' }}>
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 pt-3 text-sm">
                        {[
                          { l: 'Contact', v: `${company.contact_name}, ${company.contact_title}` },
                          { l: 'Email', v: company.admin_email },
                          { l: 'Phone', v: company.phone || '—' },
                          { l: 'Website', v: company.website || '—' },
                          { l: 'Industry', v: company.industry },
                          { l: 'Size', v: `${company.company_size} employees` },
                          { l: 'Departments', v: company.departments?.join(', ') || '—' },
                          { l: 'Location', v: [company.city, company.state, company.country].filter(Boolean).join(', ') || '—' },
                          { l: 'Submitted', v: company.submitted_at ? new Date(company.submitted_at).toLocaleDateString() : '—' },
                          { l: 'Referral', v: company.referral_source || '—' },
                          { l: 'Pilot Ends', v: pilotEnd ? pilotEnd.toLocaleDateString() : '—' },
                          { l: 'Est. Employees', v: company.employee_count_estimate || '—' },
                        ].map(f => (
                          <div key={f.l}><p className="text-[10px] uppercase tracking-wide" style={{ color: '#6B6257' }}>{f.l}</p><p className="font-medium" style={{ color: '#1A1209' }}>{f.v || '—'}</p></div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminCorporates;
